import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiService } from "@/App";
import BullishTriadCard from "@/components/BullishTriadCard";
import PerpsPricingStrip from "@/components/PerpsPricingStrip";
import PerpsPulseStrip from "@/components/PerpsPulseStrip";
import { useWallet } from "@/context/WalletContext";
import MarketIntelligenceFeed from "@/components/MarketIntelligenceFeed";
import { StacksNewsFrame } from "@/components/StacksNewsFrame";
import { StacksNetworkStats } from "@/components/StacksNetworkStats";
import { 
  Rocket, 
  Shield, 
  Zap, 
  TrendingUp, 
  Bot, 
  ArrowRight,
  Bitcoin,
  Activity,
  Lock,
  RefreshCw,
  Users,
  MessageSquare,
  Trophy,
  Wallet,
  LogOut,
  User,
  ChevronDown,
  Copy,
  LayoutDashboard,
  Compass,
  Plus,
  Eye,
  Globe,
  Droplets
} from "lucide-react";
import { toast } from "sonner";
import SwapSTXOSHIButton from "@/components/SwapSTXOSHIButton";

const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;
const ADMIN_ADDRESSES = [
  'SP3VVPEFZNN84EGMD0GN4XWAZ4X5ZMWC852NGWVWN', // Treasury (VWN)
  'SP17175BTCCNHF06Z7TNP3PG9FET3JRFQ85C31WSY', // Admin 2
  'SP1WST899N8B2VR76DCHQ56ZQ78R9VSYT2B2RNTJY', // Admin 3
  'SP2R3H4VQEHPAV8VM8KS0JR8BAS8HTANZ92R64H8K', // Admin 4 (H8K)
];

export default function Landing() {
  const navigate = useNavigate();
  const { isConnected, user, addresses, connectWallet, disconnectWallet } = useWallet();
  
  // Hydrate from localStorage cache so returning visitors see real stats instantly
  // (avoids the "stuck on 0" flash while the API call is still in flight on
  // slow networks or while production is recovering from a CF 520).
  const getCachedStats = () => {
    try {
      const cached = localStorage.getItem('stxoshi-landing-stats');
      if (cached) return JSON.parse(cached);
    } catch {}
    return null;
  };
  const getCachedPeg = () => {
    try {
      const cached = localStorage.getItem('stxoshi-landing-peg');
      if (cached) return JSON.parse(cached);
    } catch {}
    return null;
  };

  // Initialize with cached values (if any) → falls back to safe defaults on first visit
  const [stats, setStats] = useState(() => getCachedStats() || {
    total_agents: 0,
    total_users: 0,
    total_activities: 0,
    total_tvl_sbtc: 0,
    total_tvl_usd: 0
  });
  const [pegStatus, setPegStatus] = useState(() => getCachedPeg() || {
    status: 'stable',
    ratio: 1.0,
    tvl: 500
  });
  
  // Check if connected wallet is an admin
  const isAdmin = isConnected && ADMIN_ADDRESSES.includes(addresses?.stx);

  // Check for pending bot redirect (handles mobile wallet return scenario)
  useEffect(() => {
    try {
      const pendingRedirect = localStorage.getItem('stxoshi_pending_bot_redirect');
      if (pendingRedirect) {
        const { botId, botName, timestamp } = JSON.parse(pendingRedirect);
        // Only redirect if the pending redirect is less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          console.log('[LANDING] Found pending bot redirect:', botId, botName);
          // Clear the redirect info before navigating
          localStorage.removeItem('stxoshi_pending_bot_redirect');
          // Show a toast to explain what's happening
          toast.success(`Redirecting to Bot ${botName}...`, {
            description: 'Taking you to your newly deployed bot'
          });
          // Navigate to the bot dashboard
          navigate(`/bot/${botId}`, { replace: true });
          return;
        } else {
          // Clear stale redirect
          localStorage.removeItem('stxoshi_pending_bot_redirect');
        }
      }
    } catch (e) {
      console.warn('[LANDING] Error checking pending redirect:', e);
      localStorage.removeItem('stxoshi_pending_bot_redirect');
    }
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const [statsRes, pegRes] = await Promise.allSettled([
          apiService.getStats(),
          apiService.getPegStatus()
        ]);
        if (!isMounted) return;
        // Only update state/cache when fetch actually succeeded — preserves
        // previously-cached values during transient backend failures (CF 520).
        if (statsRes.status === 'fulfilled' && statsRes.value?.data) {
          setStats(statsRes.value.data);
          try { localStorage.setItem('stxoshi-landing-stats', JSON.stringify(statsRes.value.data)); } catch {}
        }
        if (pegRes.status === 'fulfilled' && pegRes.value?.data) {
          setPegStatus(pegRes.value.data);
          try { localStorage.setItem('stxoshi-landing-peg', JSON.stringify(pegRes.value.data)); } catch {}
        }
      } catch (err) {
        console.error("Failed to fetch initial data:", err);
      }
    };
    // Fire immediately on mount
    fetchData();
    // Retry quickly during the first 30s if backend was slow/down,
    // then settle into the normal 30s polling cadence.
    const quickRetries = [3000, 8000, 18000].map(delay =>
      setTimeout(() => { if (isMounted) fetchData(); }, delay)
    );
    const interval = setInterval(fetchData, 30000);
    return () => {
      isMounted = false;
      quickRetries.forEach(clearTimeout);
      clearInterval(interval);
    };
  }, []);

  const features = [
    {
      icon: <Bot className="w-6 h-6" />,
      title: "Autonomous Execution",
      description: "AI agents run 24/7, executing yield strategies without manual intervention"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "SIP-041 Identity",
      description: "Cryptographic reputation system ensures transparent, auditable agent behavior"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Multi-Protocol Yield",
      description: "Automatically harvest yields across Zest, Bitflow, ALEX, and Hermetica"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Peg Watchdog",
      description: "Real-time monitoring protects your capital during market volatility"
    }
  ];

  const protocols = [
    { name: "STXOSHI Vault", type: "High Yield 25%", isHighYield: true },
    { name: "Zest Protocol", type: "Lending" },
    { name: "Bitflow", type: "DEX" },
    { name: "ALEX", type: "Arbitrage" },
    { name: "Hermetica", type: "Vault" },
    { name: "StackingDAO", type: "Staking" },
    { name: "Velar DEX", type: "DEX" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="hero-gradient relative overflow-hidden">
        {/* Navigation */}
        <nav className="glass fixed top-0 left-0 right-0 z-50 px-3 md:px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo - Links to homepage */}
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src={STXOSHI_LOGO} 
                alt="STXOSHI" 
                className="w-8 h-8 md:w-10 md:h-10 rounded-full"
              />
              <span className="font-heading font-bold text-sm md:text-xl tracking-wider hidden sm:block">STXOSHI</span>
            </div>
            
            {/* Right side buttons - Mobile optimized order: Connect Wallet FIRST */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* CONNECT WALLET - Always visible and prominent on ALL devices */}
              {isConnected ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="text-xs md:text-sm"
                      data-testid="nav-wallet-dropdown"
                    >
                      <Avatar className="w-5 h-5 mr-1 md:mr-2">
                        <AvatarImage src={user?.avatar_url} />
                        <AvatarFallback className="text-xs bg-primary/20 text-primary">
                          {user?.username?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:inline">{addresses.stx?.slice(0, 6)}...</span>
                      <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5 text-sm font-medium">
                      {user?.username || 'My Wallet'}
                    </div>
                    <div className="px-2 py-1 text-xs text-muted-foreground font-mono">
                      {addresses.stx?.slice(0, 12)}...{addresses.stx?.slice(-8)}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => {
                        navigator.clipboard.writeText(addresses.stx);
                        toast.success('Address copied!');
                      }}
                      data-testid="copy-address-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Address
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate(`/profile/${user?.id}`)}
                      data-testid="nav-profile-btn"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profile - User Wallet
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/create')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Launch Your Agents & Bots
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Explore Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/perps')}
                      className="text-orange-400 focus:text-orange-400"
                      data-testid="header-menu-perps"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Perp Trading
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/flip-market')}
                      data-testid="header-menu-flip-market"
                    >
                      <Zap className="w-4 h-4 mr-2 text-yellow-400" />
                      Flip Prediction Market
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/lp-flips')}
                      data-testid="header-menu-lp-flips"
                    >
                      <Droplets className="w-4 h-4 mr-2 text-cyan-400" />
                      Flip Liquidity Provider
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/pulse')}
                      data-testid="header-menu-pulse"
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      STXOSHI Pulse
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/community')}>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Community
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/leaderboard')}>
                      <Trophy className="w-4 h-4 mr-2" />
                      Leaderboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/whale-leaderboard')}>
                      <Eye className="w-4 h-4 mr-2 text-purple-400" />
                      Whale Leaderboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/agent-directory')}>
                      <Globe className="w-4 h-4 mr-2 text-green-400" />
                      A2A Agent Directory
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <Bot className="w-4 h-4 mr-2" />
                      Your Agents & Bots
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/explore')}>
                      <Compass className="w-4 h-4 mr-2" />
                      Explore All Agents & Bots
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/withdrawals')}
                          className="text-amber-500 focus:text-amber-500"
                          data-testid="admin-login-btn"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Dashboard
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={disconnectWallet}
                      className="text-red-500 focus:text-red-500"
                      data-testid="disconnect-wallet-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Disconnect Wallet
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  onClick={connectWallet}
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-neon-orange text-xs md:text-sm font-bold"
                  data-testid="nav-connect-btn"
                >
                  <Wallet className="w-4 h-4 mr-1" />
                  <span>Connect Wallet</span>
                </Button>
              )}
              
              {/* Community - VISIBLE ON MOBILE (icon only) */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/community')}
                className="text-xs md:text-sm px-2 md:px-3"
                data-testid="nav-community-btn"
              >
                <MessageSquare className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">Community</span>
              </Button>
              
              {/* Dashboard - Desktop only */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="nav-dashboard-btn"
                className="hidden sm:flex text-xs md:text-sm"
              >
                Dashboard
              </Button>
              
              {/* Leaderboard - Desktop only */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/leaderboard')}
                className="hidden lg:flex"
              >
                <Trophy className="w-4 h-4 mr-1" />
                Leaderboard
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="pt-32 pb-24 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Column */}
              <div className="stagger-children">
                <div className="flex items-center gap-3 mb-6">
                  <Badge className="bg-primary/10 text-primary border-primary/20 font-tech tracking-wide">
                    BITCOIN-NATIVE AGENTIC ECONOMY
                  </Badge>
                  <Badge className="bg-green-500/10 text-green-400 border-green-500/20 font-tech tracking-wide animate-pulse">
                    MAINNET LIVE
                  </Badge>
                  <Badge
                    onClick={() => navigate("/news")}
                    data-testid="hero-news-link"
                    className="cursor-pointer bg-blue-500/10 text-blue-400 border-blue-500/30 font-tech tracking-wide hover:bg-blue-500/20 transition-colors"
                  >
                    NEWS
                  </Badge>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 mb-6">
                  <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-wider leading-none">
                    <span className="text-[#0066CC]">STXOSHI</span>
                    <br />
                    <span className="text-[#0077DD]">TREASURY</span>
                    <br />
                    <span className="text-[#00A3FF]">XCHANGE</span>
                  </h1>
                  <img
                    src={STXOSHI_LOGO}
                    alt="STXOSHI coin"
                    data-testid="hero-coin-logo"
                    className="shrink-0 h-28 w-28 md:h-40 md:w-40 lg:h-48 lg:w-48 rounded-full object-contain drop-shadow-[0_0_30px_rgba(255,120,20,0.45)]"
                  />
                </div>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-lg font-tech">
                  Deploy AI agents that manage your <span className="text-primary font-bold">real sBTC</span>. Harvest yields across DeFi protocols. 
                  Settle profits to cold storage. All on autopilot.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                  <SwapSTXOSHIButton className="w-full sm:w-auto text-lg px-8" />
                  <Button 
                    size="lg"
                    onClick={() => navigate('/perps')}
                    className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-black text-lg font-bold shadow-neon-orange px-8"
                    data-testid="hero-perps-btn"
                  >
                    <TrendingUp className="w-5 h-5 mr-2" />
                    STXOSHI Perp Trading
                  </Button>
                  <Button 
                    size="lg"
                    onClick={() => navigate('/create')}
                    className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-neon-orange text-lg font-bold px-8"
                    data-testid="hero-launch-btn"
                  >
                    <Rocket className="w-5 h-5 mr-2" />
                    Launch Your AI Bots & AI Yield Agents
                  </Button>
                  <Button 
                    size="lg"
                    onClick={() => navigate('/flip-market')}
                    className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-black text-lg font-bold px-8"
                    data-testid="hero-flip-market-btn"
                  >
                    Flip - Dynamic Parlay Prediction Market
                  </Button>
                  <Button 
                    size="lg"
                    onClick={() => navigate('/dashboard')}
                    className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-black text-lg font-bold"
                    data-testid="hero-explore-btn"
                  >
                    Explore Dashboard
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Right Column - Stats Cards */}
              <div className="grid grid-cols-2 gap-4 stagger-children">
                {/* Live market snapshot — Market Intel + Perps prices + TVL Bonds.
                    Placed above the TVL card so a mobile visitor sees the
                    ecosystem health first, right after tapping the hero CTAs. */}
                <div className="col-span-2 space-y-3">
                  <MarketIntelligenceFeed compact={true} maxItems={8} />
                  <PerpsPricingStrip />
                  <BullishTriadCard />
                </div>

                <Card className="glass-card card-hover col-span-2" data-testid="stat-tvl-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Bitcoin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Value Locked</p>
                        <p className="font-mono text-3xl font-bold text-foreground">
                          {stats?.total_tvl_sbtc?.toFixed ? `${stats.total_tvl_sbtc.toFixed(4)} sBTC` : '0.0000 sBTC'}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground font-mono">
                      ≈ ${stats?.total_tvl_usd?.toLocaleString ? stats.total_tvl_usd.toLocaleString() : '0'} USD
                    </p>
                  </CardContent>
                </Card>

                <Card className="glass-card card-hover" data-testid="stat-agents-card">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center mb-3">
                      <Bot className="w-5 h-5 text-secondary" />
                    </div>
                    <p className="text-sm text-muted-foreground">Active Bots & Agents</p>
                    <p className="font-mono text-2xl font-bold">{stats?.total_agents || 0}</p>
                  </CardContent>
                </Card>

                <Card className="glass-card card-hover" data-testid="stat-activities-card">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-3">
                      <Activity className="w-5 h-5 text-accent" />
                    </div>
                    <p className="text-sm text-muted-foreground">Total Activities</p>
                    <p className="font-mono text-2xl font-bold">{stats?.total_activities || 0}</p>
                  </CardContent>
                </Card>

                <Card className="glass-card card-hover col-span-2" data-testid="peg-health-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">sBTC/BTC Peg Health</p>
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${pegStatus?.status === 'stable' ? 'bg-green-500' : 'bg-yellow-500'} pulse-live`}></span>
                          <span className={`font-mono text-lg ${pegStatus?.status === 'stable' ? 'text-green-400' : 'text-yellow-400'}`}>
                            {pegStatus?.status?.toUpperCase() || 'CHECKING'}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Signer Status</p>
                        <p className="font-mono text-green-400">{pegStatus?.signer_health || '---'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6">
                <PerpsPulseStrip />
              </div>
            </div>
          </div>
        </div>
      </div>

      <StacksNewsFrame />
      <StacksNetworkStats />

      {/* Features Section */}
      <section className="py-12 px-6 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20 font-tech tracking-wide">
              SOVEREIGN INFRASTRUCTURE
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-wider mb-4">
              YOUR AGENT, YOUR KEYS
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-tech">
              Unlike centralized trading bots, your Treasury Agent operates with full sovereignty—managing its own keys and auditable on-chain.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="glass-card card-hover group"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <div className="text-primary">{feature.icon}</div>
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Protocols Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/20 font-tech tracking-wide">
              DEFI INTEGRATIONS
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-wider mb-4">
              MULTI-PROTOCOL YIELD
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-tech">
              Your agent automatically routes capital across the best yield opportunities in the Stacks ecosystem.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {protocols.map((protocol, index) => (
              <div 
                key={index}
                className={`glass-card px-6 py-4 rounded-full flex items-center gap-3 ${
                  protocol.isHighYield 
                    ? 'ring-2 ring-amber-500/50 bg-gradient-to-r from-amber-500/10 to-orange-500/10' 
                    : ''
                }`}
                data-testid={`protocol-badge-${index}`}
              >
                {protocol.isHighYield ? (
                  <span className="text-amber-400">🔥</span>
                ) : (
                  <RefreshCw className="w-4 h-4 text-primary" />
                )}
                <span className={`font-medium ${protocol.isHighYield ? 'text-amber-200' : ''}`}>
                  {protocol.name}
                </span>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    protocol.isHighYield 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 font-bold' 
                      : ''
                  }`}
                >
                  {protocol.type}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-24 pb-8 px-6 hero-gradient">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-wider mb-6">
            READY TO DEPLOY YOUR
            <br />
            <span className="text-primary">TREASURY AGENT?</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto font-tech">
            Configure your agent's SOUL.md, fund it with sBTC, and let it work 24/7 to grow your Bitcoin.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center mb-8">
            <SwapSTXOSHIButton className="w-full sm:w-auto text-lg px-8" />
            <Button 
              size="lg"
              onClick={() => navigate('/perps')}
              className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-black text-lg font-bold shadow-neon-orange px-8"
              data-testid="cta-perps-btn"
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              STXOSHI Perp Trading
            </Button>
            <Button 
              size="lg"
              onClick={() => navigate('/create')}
              className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-neon-orange text-lg font-bold px-8"
              data-testid="cta-launch-btn"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Launch Your AI Bots & AI Yield Agents
            </Button>
            <Button 
              size="lg"
              onClick={() => navigate('/flip-market')}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-black text-lg font-bold px-8"
              data-testid="cta-flip-market-btn"
            >
              Flip - Dynamic Parlay Prediction Market
            </Button>
            <Button 
              size="lg"
              onClick={() => navigate('/dashboard')}
              className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-black text-lg font-bold"
              data-testid="cta-explore-btn"
            >
              Explore Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - with extra bottom padding to avoid "Made with Emergent" badge */}
      <footer className="py-8 px-6 border-t border-border pb-20">
        <div className="max-w-7xl mx-auto flex flex-col gap-6">
          {/* Logo and Company */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src={STXOSHI_LOGO} 
                alt="STXOSHI" 
                className="w-8 h-8 rounded-full"
              />
              <div>
                <span className="font-heading font-bold tracking-wider">STXOSHI TREASURY</span>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a 
                href="https://x.com/stxoshi_TOK" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                title="Follow us on X"
                data-testid="footer-x-link"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a 
                href="https://t.me/stxoshiYieldAI" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                title="Join our Telegram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </a>
              <a 
                href="https://discord.gg/5j5URKmVTD" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                title="Join our Discord"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                </svg>
              </a>
              <a 
                href="https://primal.net/p/npub1qxr8yx6jmql2397fx97hup3p4ptw48q8jzaxas9aa6yxpmv8fv2sx5c6rc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                title="Follow us on Nostr"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              </a>
              <a 
                href="https://substack.com/@stxoshi" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-primary transition-colors"
                title="Subscribe on Substack"
                data-testid="footer-substack-link"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Tagline */}
          <p className="text-sm text-muted-foreground font-mono tracking-wide text-center">
            STX/sBTC AUTONOMOUS BITCOIN TREASURY AGENT • POWERED BY STACKS • GEMINI 3 FLASH •{" "}
            <a href="/news" onClick={(e) => { e.preventDefault(); navigate("/news"); }} data-testid="footer-news-link" className="text-primary hover:underline">
              NEWS
            </a>{" "}•{" "}
            <a href="/support" onClick={(e) => { e.preventDefault(); navigate("/support"); }} data-testid="footer-support-link" className="text-primary hover:underline">
              SUPPORT TICKET
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
